import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import { showTransientNotification } from '@driftt/traffic-control'
import * as actions from './actions'
import { fetchSettings, fetchCustomLinks } from 'modules/settings/actions'
import { api } from './api'
import { map, startWith, mergeMap, catchError } from 'rxjs/operators'

import {
  getArticle,
  getCategory,
  getSectionToDelete,
  getSectionToEdit,
} from './selectors'

import {
  newCategorySuccessConfig,
  newSectionSuccessConfig,
  editSuccessConfig,
  newSectionFailureConfig,
  newArticleFailureConfig,
  editFailureConfig,
  newCategoryFailureConfig,
  deleteSuccessConfig,
  deleteFailureConfig,
  fetchCategoriesFailureConfig,
  reorderCategoriesSuccessConfig,
  reorderCategoriesFailureConfig
} from './model'

const titleErrorMessage = 'This title is already taken - please use a new one.'
const slugErrorMessage = 'This URL is already taken - please use a new URL.'

const createArticle = (action$, store) => {
  return action$
    .ofType(actions.createArticle.toString())
    .mergeMap(() => {
      const article = getArticle(store.getState())
      return api
        .createArticle(article)
        .pipe(
          map(() => {
            window.location.pathname = `/help/article/${article.slug}`
            return actions.createArticleSuccess()
          }),
          startWith(actions.createArticlePending()),
          catchError(({ response }) => {
            if (response.Error === 'Slug already exists') {
              return Observable.of(
                actions.checkSlugFailed({slug: true, message: slugErrorMessage}),
              )
            }
            
            if (response.Error === 'Title already exists') {
              return Observable.of(
                actions.createArticleFailed({title: true, message: titleErrorMessage}),
              )
            }
              
            return Observable.of(
              actions.createArticleFailed(),
              showTransientNotification(newArticleFailureConfig)
            )
          })
        )
      })
  }


const checkTitle = (action$, store) => {
  return action$
    .ofType(actions.checkTitle.toString())
    .debounceTime(1000)
    .pluck('payload')
    .mergeMap(payload => {
      return api
        .checkTitle(payload)
        .pipe(
          map(actions.checkTitleSuccess),
          catchError(() => Observable.of(
            actions.checkTitleFailed({title: true, message: titleErrorMessage}),
          ))
        )
      })
  }

const checkSlug = (action$, store) => {
  return action$
  .ofType(actions.checkSlug.toString())
  .debounceTime(1000)
  .pluck('payload')
  .mergeMap(payload => {
    return api
    .checkSlug(payload)
    .pipe(
      map(actions.checkSlugSuccess),
      catchError(() => Observable.of(
        actions.checkSlugFailed({slug: true, message: slugErrorMessage}),
      ))
    )
  })
}

const deleteArticle = (action$, store) => {
  return action$
  .ofType(actions.deleteArticle.toString())
  .mergeMap(() => {
    const article = getArticle(store.getState())
    return api
      .deleteArticle(article)
      .pipe(
        mergeMap(() => {
          if (article.isBeingDeletedFromEditor) window.location.pathname = '/help'
          return [
            actions.deleteArticleSuccess(),
            showTransientNotification(deleteSuccessConfig)
          ]
        }),
        startWith(actions.deleteArticlePending()),
        catchError(() => Observable.of(
          actions.deleteArticleFailed(),
          showTransientNotification(deleteFailureConfig)
        ))
      )
  })
}

const createCategory = (action$, store) => {
  return action$
    .ofType(actions.createCategory.toString())
    .mergeMap(() => {
      const category = getCategory(store.getState())
      return api
        .createCategory(category)
        .pipe(
          mergeMap((category) =>
          [
            actions.createCategorySuccess(category),
            showTransientNotification(newCategorySuccessConfig)
          ]),
          startWith(actions.createCategoryPending()),
          catchError(() => Observable.of(
            actions.createCategoryFailed(),
            showTransientNotification(newCategoryFailureConfig)
          ))
        )
  })
}

const editCategory = (action$, store) => {
  return action$
  .ofType(actions.editCategory.toString())
  .mergeMap(() => {
    const category = getCategory(store.getState())
    return api
    .editCategory(category)
    .pipe(
      mergeMap((category) => [
        actions.editCategorySuccess(category),
        showTransientNotification(editSuccessConfig)
      ]),
      startWith(actions.editCategoryPending()),
      catchError(() => Observable.of(
        actions.editCategoryFailed(),
        showTransientNotification(editFailureConfig)
      ))
    )
  })
}

const deleteCategory = (action$, store) => {
  return action$
  .ofType(actions.deleteCategory.toString())
  .mergeMap(() => {
    const category = getCategory(store.getState())
    return api
    .deleteCategory(category)
    .pipe(
      mergeMap(() => [
        actions.deleteCategorySuccess(),
        showTransientNotification(deleteSuccessConfig)
      ]),
      startWith(actions.deleteCategoryPending()),
      catchError(() => Observable.of(
        actions.deleteCategoryFailed(),
        showTransientNotification(deleteFailureConfig)
      ))
    )
  })
}

const reorderCategories = action$ => {
  return action$
    .ofType(actions.reorderCategories.toString())
    .pluck('payload')
    .mergeMap(payload => {
      return api
      .reorderCategories(payload)
      .pipe(
        mergeMap((categories) => [
          actions.reorderCategoriesSuccess(categories),
          showTransientNotification(reorderCategoriesSuccessConfig)
        ]),
        startWith(actions.reorderCategoriesPending()),
        catchError(() => Observable.of(
          actions.reorderCategoriesFailed(),
          showTransientNotification(reorderCategoriesFailureConfig)
        ))
      )
  })
}

const reorderSections = action$ => {
  return action$
  .ofType(actions.reorderCategorySections.toString())
  .pluck('payload')
  .mergeMap(payload => {
    return api
    .reorderSections(payload)
    .pipe(
      mergeMap((sections) => [
        actions.reorderCategorySectionsSuccess(sections),
        showTransientNotification(reorderCategoriesSuccessConfig)
      ]),
      startWith(actions.reorderCategorySectionsPending()),
      catchError(() => Observable.of(
        actions.reorderCategorySectionsFailed(),
        showTransientNotification(reorderCategoriesFailureConfig)
      ))
    )
  })
}

const reorderArticles = action$ => {
  return action$
  .ofType(actions.reorderCategoryDocuments.toString(), actions.reorderSectionDocuments.toString())
  .pluck('payload')
  .mergeMap(payload => {
    return api
    .reorderArticles(payload)
    .pipe(
      mergeMap((articles) => [
        actions.reorderCategoryDocumentsSuccess(articles),
        showTransientNotification(reorderCategoriesSuccessConfig)
      ]),
      startWith(actions.reorderCategoryDocumentsPending()),
      catchError(() => Observable.of(
        actions.reorderCategoryDocumentsFailed(),
        showTransientNotification(reorderCategoriesFailureConfig)
      ))
    )
  })
}

const editArticle = (action$, store) => {
  return action$
  .ofType(actions.editArticle.toString())
  .mergeMap(() => {
    const article = getArticle(store.getState())
    article.updatedAt = null
    return api
    .editArticle(article)
    .pipe(
      mergeMap((article) => [
        actions.editArticleSuccess(article),
        showTransientNotification(editSuccessConfig)
      ]),
      startWith(actions.editArticlePending()),
      catchError(() => Observable.of(
        actions.editArticleFailed(),
        showTransientNotification(editFailureConfig)
      ))
    )
  })
  
}

const fetchArticle = action$ => {
  return action$
    .ofType(actions.fetchArticle.toString())
    .pluck('payload')
    .mergeMap(payload => {
      return api
      .fetchArticle(payload)
      .pipe(
        map(actions.fetchArticleSuccess),
        startWith(actions.fetchArticlePending()),
        catchError(() => Observable.of(actions.fetchArticleFailed()))
      )
    })
}

const fetchCategories = action$ => {
  return action$
    .ofType(actions.fetchCategories.toString())
    .mergeMap(() => {
      return api
      .fetchCategories()
      .pipe(
        mergeMap((res) => [
          actions.fetchCategoriesSuccess(res),
          fetchSettings(),
          fetchCustomLinks()
        ]),
        startWith(actions.fetchCategoriesPending()),
        catchError(() => Observable.of(
          actions.fetchCategoriesFailed(),
          showTransientNotification(fetchCategoriesFailureConfig)
        ))
      )
  })
}

const createSection = (action$, store) => {
  return action$
  .ofType(actions.createSection.toString())
  .mergeMap(() => {
    const section = getSectionToEdit(store.getState())
    return api
      .createSection(section)
      .pipe(
        mergeMap((section) => [
          actions.createSectionSuccess(section),
          showTransientNotification(newSectionSuccessConfig)
        ]),
        startWith(actions.createSectionPending()),
        catchError(() => Observable.of(
          actions.createSectionFailed(),
          showTransientNotification(newSectionFailureConfig)
        ))
      )
  })
}

const editSection = (action$, store) => {
  return action$
  .ofType(actions.saveSectionEdits.toString())
  .mergeMap(() => {
    const section = getSectionToEdit(store.getState())
    return api
    .saveSectionEdits(section)
    .pipe(
      mergeMap((section) => [
        actions.saveSectionEditsSuccess(section),
        showTransientNotification(editSuccessConfig)
      ]),
      startWith(actions.saveSectionEditsPending()),
      catchError(() => Observable.of(actions.saveSectionEditsFailed(), showTransientNotification(editFailureConfig)))
    )
  })
}

const deleteSection = (action$, store) => {
  return action$
    .ofType(actions.deleteSection.toString())
    .mergeMap(() => {
      const section = getSectionToDelete(store.getState())
      return api
      .deleteSection(section)
      .pipe(
        mergeMap((section) => [
          actions.deleteSectionSuccess(section),
          showTransientNotification(deleteSuccessConfig)
        ]),
        startWith(actions.deleteSectionPending()),
        catchError(() => Observable.of(
          actions.deleteSectionFailed(),
          showTransientNotification(deleteFailureConfig)
        ))
      )
    })
}

export const epic = combineEpics(
  createArticle,
  reorderCategories,
  reorderSections,
  reorderArticles,
  checkTitle,
  checkSlug,
  deleteArticle,
  createCategory,
  editCategory,
  deleteCategory,
  editArticle,
  fetchArticle,
  fetchCategories,
  createSection,
  editSection,
  deleteSection
)
